import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, Scan, ScanResult } from '../../services/api';
import { format } from 'date-fns';
import { EvidenceDisplay, ConfidenceBadge } from './EvidenceDisplay';
import { EvidenceMetricsCard } from './EvidenceMetricsCard';

const severityIcons = {
  critical: <WarningIcon sx={{ color: '#B91C1C' }} />,
  high: <WarningIcon sx={{ color: '#DC2626' }} />,
  medium: <InfoIcon sx={{ color: '#F59E0B' }} />,
  low: <CheckCircleIcon sx={{ color: '#16A34A' }} />,
};

const severityColors = {
  critical: '#B91C1C',
  high: '#DC2626',
  medium: '#F59E0B',
  low: '#16A34A',
} as const;

interface IssueRowProps {
  issue: ScanResult;
  index: number;
  scanId: string;
  onReportSubmitted: () => void;
  canReportFalsePositive: boolean;
}

function IssueRow({ issue, index, scanId, onReportSubmitted, canReportFalsePositive }: IssueRowProps) {
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState<string>('incorrect_detection');
  const [reportDescription, setReportDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReportClick = () => {
    setReportDialogOpen(true);
    setError(null);
  };

  const handleReportClose = () => {
    setReportDialogOpen(false);
    setReportReason('incorrect_detection');
    setReportDescription('');
    setError(null);
  };

  const handleSubmitReport = async () => {
    try {
      setSubmitting(true);
      setError(null);

      await apiClient.reportFalsePositive({
        scanId,
        ruleId: issue.ruleId || (issue as any).rule_id || (issue as any).id || 'unknown',
        ruleName: issue.title || 'Unknown Rule',
        ruleDescription: issue.description,
        reason: reportReason as any,
        description: reportDescription || undefined,
        context: {
          file: issue.file,
          line: issue.line,
          column: issue.column,
          framework: issue.framework,
        },
      });

      handleReportClose();
      onReportSubmitted();
    } catch (err: any) {
      console.error('Error submitting false positive report:', err);
      setError(err.response?.data?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
            {issue.status === 'pass' ? (
              <CheckCircleIcon color="success" />
            ) : (
              severityIcons[issue.severity]
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" fontWeight="medium">
                {issue.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {issue.status !== 'pass' && (
                  <Chip
                    label={issue.severity}
                    size="small"
                    sx={{ bgcolor: severityColors[issue.severity], color: '#fff' }}
                  />
                )}
                {issue.file && (
                  <Chip
                    label={`${issue.file}${issue.line ? `:${issue.line}` : ''}`}
                    variant="outlined"
                    size="small"
                  />
                )}
                {issue.confidence !== undefined && <ConfidenceBadge confidence={issue.confidence} />}
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '2 1 400px', minWidth: 300 }}>
              <Typography variant="body2" paragraph>
                <strong>Description:</strong>{' '}
                {issue.status === 'pass' ? (
                  <Box component="span" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {issue.description}
                  </Box>
                ) : (
                  issue.description
                )}
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Rule ID:</strong> {issue.ruleId || (issue as any).rule_id || (issue as any).id || 'N/A'}
              </Typography>
              {issue.file && (
                <Typography variant="body2" gutterBottom>
                  <strong>File:</strong> {issue.file}
                </Typography>
              )}
              {issue.line && (
                <Typography variant="body2" gutterBottom>
                  <strong>Line:</strong> {issue.line}
                  {issue.column && `, Column: ${issue.column}`}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Evidence Display */}
          {/* <EvidenceDisplay
            evidenceSummary={issue.evidenceSummary}
            fieldSuggestions={issue.fieldSuggestions}
            confidence={issue.confidence}
          /> */}
          
          {issue.status !== 'pass' && canReportFalsePositive && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                size="small"
                startIcon={<FlagIcon />}
                onClick={handleReportClick}
                variant="outlined"
                color="warning"
              >
                Report False Positive
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Report False Positive Dialog */}
      <Dialog open={reportDialogOpen} onClose={handleReportClose} maxWidth="sm" fullWidth>
        <DialogTitle>Report False Positive</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Help us improve by reporting this detection as a false positive.
            </Typography>
            
            <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
              <strong>Rule:</strong> {issue.ruleId || (issue as any).rule_id || (issue as any).id || 'N/A'}
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Reason</InputLabel>
              <Select
                value={reportReason}
                label="Reason"
                onChange={(e) => setReportReason(e.target.value)}
              >
                <MenuItem value="incorrect_detection">Incorrect Detection</MenuItem>
                <MenuItem value="not_applicable">Not Applicable to My Use Case</MenuItem>
                <MenuItem value="environment_specific">Environment-Specific Configuration</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Details (Optional)"
              placeholder="Please provide any additional context that might help us understand why this is a false positive..."
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReportClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReport}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <FlagIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function ScanDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scan, setScan] = useState<Scan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [canReportFalsePositive, setCanReportFalsePositive] = useState(false);
  const [fullResults, setFullResults] = useState<ScanResult[] | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setResultsError(null);
        
        // Fetch scan data
        const scanData = await apiClient.getScan(id);
        setScan(scanData);

        // Check user's plan to determine if they can report false positives
        try {
          const billingData = await apiClient.getMyBilling();
          const planId = billingData.plan?.id?.toLowerCase();
          const isPaid = planId !== 'free';
          setCanReportFalsePositive(isPaid);

          // Paid plans: fetch full results directly through backend
          if (isPaid) {
            try {
              setResultsLoading(true);
              const data = await apiClient.getScanResults(id);
              const results = Array.isArray((data as any)?.results) ? (data as any).results : [];
              setFullResults(results);
            } catch (e: any) {
              console.error('Error loading full scan results:', e);
              setResultsError(e?.message || 'Failed to load full scan results');
              setFullResults(null);
            } finally {
              setResultsLoading(false);
            }
          } else {
            setFullResults(null);
          }
        } catch (billingErr) {
          console.error('Error fetching billing info:', billingErr);
          // Default to not allowing if we can't fetch billing
          setCanReportFalsePositive(false);
          setFullResults(null);
          setResultsError(null);
        }
      } catch (err: any) {
        console.error('Error fetching scan:', err);
        setError('Failed to load scan details');
      } finally {
        setLoading(false);
        setResultsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleBack = () => {
    navigate('/scans');
  };

  const handleReportSubmitted = () => {
    setSnackbarMessage('False positive report submitted successfully! Our team will review it.');
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Scans
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!scan) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Scans
        </Button>
        <Alert severity="info">Scan not found</Alert>
      </Box>
    );
  }

  const getRiskScoreColor = (score: number) => {
    if (score > 30) return 'error';
    if (score > 5) return 'warning';
    return 'success';
  };

  const results = fullResults ?? scan.results ?? [];

  // Group issues by framework (only failed/error results, not passed)
  const issuesByFramework = results
    .filter(r => r.status === 'fail' || r.status === 'error')
    .reduce((acc, issue) => {
      if (!acc[issue.framework]) {
        acc[issue.framework] = [];
      }
      acc[issue.framework].push(issue);
      return acc;
    }, {} as Record<string, ScanResult[]>);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Scans
      </Button>

      {/* Critical Issues Alert */}
      {(scan._hasCritical || (scan.summary?.failedBySeverity?.critical ?? 0) > 0) && (
        <Alert 
          severity="error" 
          icon={<WarningIcon />}
          sx={{ mb: 3, fontWeight: 'medium' }}
        >
          <Typography variant="body1" fontWeight="bold">
            ⚠️ CRITICAL ISSUES FOUND
          </Typography>
          <Typography variant="body2">
            This scan found {scan.summary?.failedBySeverity?.critical ?? 0} critical security {(scan.summary?.failedBySeverity?.critical ?? 0) === 1 ? 'issue' : 'issues'} that require immediate attention.
          </Typography>
        </Alert>
      )}

      {/* Scan Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                {scan.scanId}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {scan.scanType} scan • {format(new Date(scan.timestamp), 'MMMM dd, yyyy HH:mm')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {(() => {
                const complianceRate = scan.risk?.complianceRate ?? 
                  ((scan.summary?.totalRules ?? 0) > 0 
                    ? ((scan.summary?.passed ?? 0) / (scan.summary?.totalRules ?? 1)) * 100 
                    : 100);
                
                const getComplianceBadge = (rate: number) => {
                  if (rate >= 95) return { label: '🏆 Excellent', color: 'success' as const };
                  if (rate >= 85) return { label: '✅ Good', color: 'success' as const };
                  if (rate >= 70) return { label: '⚠️ Fair', color: 'warning' as const };
                  return { label: '❌ Poor', color: 'error' as const };
                };
                const badge = getComplianceBadge(complianceRate);
                return (
                  <Chip
                    label={badge.label}
                    color={badge.color}
                    sx={{ fontWeight: 'bold' }}
                  />
                );
              })()}
              <Tooltip
                title={!canReportFalsePositive ? 'Upgrade to download the detailed output' : ''}
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!id || !canReportFalsePositive}
                    onClick={async () => {
                      if (!id || !canReportFalsePositive) return;
                      try {
                        // Fetch the results data with auth
                        const data = await apiClient.getScanResults(id);
                        // Create a blob and download it
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `baastream_${scan.scanId}_results.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error('Failed to download results:', error);
                      }
                    }}
                  >
                    Download JSON
                  </Button>
                </span>
              </Tooltip>
              <Chip
                label={scan.status}
                color={scan.status === 'completed' ? 'success' : 'default'}
              />
            </Stack>
          </Box>

          {canReportFalsePositive && resultsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading full results…
              </Typography>
            </Box>
          )}
          {resultsError && <Alert severity="error" sx={{ mb: 2 }}>{resultsError}</Alert>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <Typography variant="h3" color="success.main" fontWeight="bold">
                {(() => {
                  let rate = scan.risk?.complianceRate;
                  if (rate === undefined || rate === null) {
                    if (scan.summary?.totalRules && scan.summary.totalRules > 0) {
                      const passed = scan.summary.passed ?? 0;
                      rate = (passed / scan.summary.totalRules) * 100;
                    } else if (scan.summary?.totalRules === 0) {
                      // If no rules were run, consider it 100% compliant
                      rate = 100;
                    } else {
                      // Infer from risk score (inverse)
                      const riskScore = scan.risk.overallScore;
                      rate = 100 - riskScore;
                    }
                  }
                  
                  return rate.toFixed(0);
                })()}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compliance Rate
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <Typography variant="h3" color={`${getRiskScoreColor(scan.risk?.overallScore ?? 0)}.main`} fontWeight="bold">
                {scan.risk?.overallScore ?? 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Risk Score
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {scan.summary?.passed ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Passed
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="error.main">
                {scan.summary?.failed ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="warning.main">
                {scan.summary?.errors ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Errors
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="text.secondary">
                {scan.summary?.skipped ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Skipped
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Severity Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Issues by Severity
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(scan.summary?.bySeverity ?? {}).map(([severity, count]) => (
                <Box key={severity} sx={{ flex: '1 1 150px', textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ color: severityColors[severity as keyof typeof severityColors] }}
                  >
                    {count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textTransform="capitalize">
                    {severity}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

      {/* Evidence Metrics Summary */}
      {scan.evidenceMetrics && (
        <EvidenceMetricsCard metrics={scan.evidenceMetrics} />
      )}

      {/* Passed Rules Summary (only show if all passed or some passed) */}
      {results.length > 0 && results.filter(r => r.status === 'pass').length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                Passed Rules ({results.filter(r => r.status === 'pass').length})
              </Typography>
              <Chip 
                label={`${((results.filter(r => r.status === 'pass').length / results.length) * 100).toFixed(0)}% Compliant`}
                color="success" 
                size="small"
              />
            </Box>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  View all {results.filter(r => r.status === 'pass').length} rules that passed
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {results
                    .filter(r => r.status === 'pass')
                    .map((rule, index) => (
                      <Box 
                        key={`passed-${index}`}
                        sx={{ 
                          py: 1.5, 
                          px: 2,
                          borderBottom: index < results.filter(r => r.status === 'pass').length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                          <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {rule.ruleId || (rule as any).rule_id || (rule as any).id || 'N/A'}: {rule.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip 
                                label={rule.severity}
                                size="small"
                                sx={{ 
                                  bgcolor: 'success.50',
                                  color: 'success.700',
                                  textTransform: 'uppercase',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                              {rule.framework && (
                                <Chip 
                                  label={rule.framework}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                              {rule.category && (
                                <Chip 
                                  label={rule.category}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Issues by Framework */}
      {Object.keys(issuesByFramework).length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Issues by Framework
            </Typography>
            {Object.entries(issuesByFramework).map(([framework, issues]) => (
              <Box key={framework} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <SecurityIcon />
                  <Typography variant="h6">{framework && framework !== 'undefined' && framework !== 'null' && framework.trim() !== ''
                    ? framework
                    : 'All Frameworks'}</Typography>
                  <Chip label={`${issues.length} issues`} size="small" />
                </Box>
                <Box sx={{ ml: 4 }}>
                  {issues.map((issue, index) => (
                    <IssueRow 
                      key={`${framework}-${index}`} 
                      issue={issue} 
                      index={index}
                      scanId={scan.scanId}
                      onReportSubmitted={handleReportSubmitted}
                      canReportFalsePositive={canReportFalsePositive}
                    />
                  ))}
                </Box>
                {Object.keys(issuesByFramework).indexOf(framework) < Object.keys(issuesByFramework).length - 1 && (
                  <Divider sx={{ mt: 3 }} />
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {results.length === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Issues Found
              </Typography>
              <Typography color="text.secondary">
                This scan completed successfully with no compliance issues detected.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
